import FocusTrap from 'focus-trap-react';
import { motion } from 'framer-motion';
import { useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useRecoilValue } from 'recoil';

import { Icon } from '@/components/icon';
import { DRAWER_PORTAL_ROOT_ID } from '@/lib/constants';
import { PortalContainer } from '@/lib/context/portal-container';
import { appNavState, mobileAppNavIsOpenState } from '@/state/navigation';
import { filtersPanelOpenState } from '@/state/routes';
import { tv } from '@/utils/styles';

export interface DrawerProps {
  children: React.ReactNode;
  closeDrawer: () => void;
  isOpen: boolean;
  pageHasFilters?: boolean;
  pageHasTopNav?: boolean;
  showCloseButton?: boolean;
  width?: string;
  variant?: 'single' | 'stack';
  hasLighterOverlay?: boolean;
  className?: string;
}

const Drawer = ({
  children,
  closeDrawer,
  isOpen,
  pageHasFilters = false,
  pageHasTopNav = false,
  showCloseButton = false,
  width,
  variant = 'stack',
  hasLighterOverlay,
  className,
}: DrawerProps) => {
  const drawerPortalRoot = typeof document !== 'undefined' && document.getElementById(DRAWER_PORTAL_ROOT_ID);
  const filtersAreOpen = useRecoilValue(filtersPanelOpenState);
  const appNav = useRecoilValue(appNavState);
  const mobileNavIsOpen = useRecoilValue(mobileAppNavIsOpenState);
  const appNavIsOpen = appNav?.isOpen;

  const triggerClose = useCallback(() => {
    if (isOpen) closeDrawer();
  }, [closeDrawer, isOpen]);

  // This event is called when ESC is pressed (which also deactivates the Trap)
  const handleOnDeactivate = () => {
    // @TODO: determine why this breaks and throws an error when the click events don't
    // triggerClose();
  };

  const { dialog, inner, overlay, scroller, base, closeButton } = styles({
    pageHasFilters,
    filtersAreOpen,
    variant,
    hasLighterOverlay,
    appNavIsOpen,
    mobileNavIsOpen,
    pageHasTopNav,
  });

  const drawer = (
    <FocusTrap
      focusTrapOptions={{
        onPostDeactivate: handleOnDeactivate,
        clickOutsideDeactivates: true,
        fallbackFocus: `#${DRAWER_PORTAL_ROOT_ID}`,
      }}
    >
      <div className={base({ className })} aria-modal="true">
        <motion.div
          onClick={() => triggerClose()}
          initial="hidden"
          exit="hidden"
          animate={isOpen ? 'open' : 'hidden'}
          variants={{
            open: { opacity: 1 },
            hidden: { opacity: 0 },
          }}
          transition={{ duration: 0.6 }}
          className={overlay()}
        />
        <div className={inner()} style={{ width }}>
          <motion.div
            initial="hidden"
            exit="hidden"
            animate={isOpen ? 'open' : 'hidden'}
            variants={{
              open: { left: 0 },
              hidden: { left: '100%' },
            }}
            transition={{ duration: 0.6 }}
            className={dialog()}
          >
            <div className={scroller()} style={{ width }}>
              <PortalContainer>{children}</PortalContainer>
            </div>
            {showCloseButton && (
              <button className={closeButton()} type="button" aria-label="close" onClick={() => triggerClose()}>
                <Icon name="close" />
              </button>
            )}
          </motion.div>
        </div>
      </div>
    </FocusTrap>
  );

  return isOpen && drawerPortalRoot ? ReactDOM.createPortal(drawer, drawerPortalRoot) : null;
};

const styles = tv({
  slots: {
    base: 'fixed bottom-0 left-0 z-7 translate-x-0 content-center items-start p-0 transition-all duration-300 ease-in-out backface-hidden max-md:top-[var(--app-mobile-header-height)] md:right-12 md:top-0 md:ml-14',
    dialog: 'relative z-3 m-auto w-full translate-x-0 rounded-tl-lg bg-grey-50 backface-hidden',
    inner:
      'drawer-inner relative left-0 right-0 z-3 ml-auto mr-0 translate-x-0 transition-all duration-300 ease-in-out backface-hidden',
    scroller:
      'h-screen translate-x-0 overflow-y-auto rounded-tl-lg p-0 backface-hidden max-md:h-[calc(100vh-var(--app-mobile-header-height))]',
    overlay: 'fixed bottom-0 left-0 right-0 top-0 translate-x-0 bg-[rgba(0,0,0,0.5)] backface-hidden',
    closeButton:
      'absolute left-md top-md grid h-12 w-12 -translate-x-20 cursor-pointer place-items-center rounded-full bg-[#222] transition duration-150 ease-in-out [&_svg]:h-md [&_svg]:w-md [&_svg]:fill-white',
  },
  variants: {
    variant: {
      single: {
        base: 'right-0 md:right-0',
      },
      stack: {
        base: '[&:last-child>.drawer-inner]:md:left-12 [&:not(:last-child)>.drawer-inner]:md:left-6',
      },
    },
    pageHasFilters: {
      true: {
        base: 'ml-10',
      },
    },
    pageHasTopNav: {
      true: {
        base: '!top-[var(--app-header-height)]',
        scroller: 'h-[calc(100vh-var(--app-header-height))]',
      },
    },
    filtersAreOpen: {
      true: {
        base: 'right-0 ml-[var(--filters-side-panel-width)] [&:last-child>.drawer-inner]:left-0',
      },
    },
    appNavIsOpen: {
      true: {
        base: 'right-0 md:ml-[var(--app-nav-width)] [&:last-child>.drawer-inner]:left-0',
      },
      false: {
        base: 'delay-[90ms]',
      },
    },
    mobileNavIsOpen: {
      true: {
        base: 'max-md:translate-x-8 max-md:blur-[2px]',
      },
      false: {
        base: 'translate-x-0',
      },
    },
    hasLighterOverlay: {
      true: {
        scroller: 'shadow-lg',
        overlay: 'bg-[rgba(0,0,0,0.1)]',
      },
    },
  },
});

export { Drawer };
